<template>
  <div class="admin-container">
    <header class="admin-header">
      <div class="header-content">
        <div class="header-title">
          <h1>Painel Administrativo</h1>
          <span class="subtitle">Gestão de Cotações</span>
        </div>
        <button @click="logout" class="btn-logout">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
            <polyline points="16,17 21,12 16,7"></polyline>
            <line x1="21" y1="12" x2="9" y2="12"></line>
          </svg>
          Sair
        </button>
      </div>
    </header>

    <main class="admin-main">
      <div class="filters-card">
        <div class="card-header">
          <h2>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <polygon points="22,3 2,3 10,12.46 10,19 14,21 14,12.46 22,3"></polygon>
            </svg>
            Filtros
          </h2>
        </div>
        <form @submit.prevent="applyFilters" class="filters-form">
          <div class="filter-group">
            <label for="startDate">Data Inicial</label>
            <input id="startDate" v-model="filters.startDate" type="date" required class="filter-input" />
          </div>

          <div class="filter-group">
            <label for="endDate">Data Final</label>
            <input id="endDate" v-model="filters.endDate" type="date" required class="filter-input" />
          </div>

          <div class="filter-group">
            <label for="situation">Situação</label>
            <select id="situation" v-model="filters.situation" required class="filter-input">
              <option value="1">Recebido</option>
              <option value="2">Cotando</option>
              <option value="3">Cotação Concluída</option>
              <option value="4">Aprovado</option>
              <option value="5">Rejeitado</option>
              <option value="6">Arquivado</option>
            </select>
          </div>

          <div class="filter-actions">
            <button type="submit" class="btn-search" :disabled="loading">
              <svg v-if="!loading" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <circle cx="11" cy="11" r="8"></circle>
                <path d="M21 21l-4.35-4.35"></path>
              </svg>
              <svg v-else width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="spinner">
                <line x1="12" y1="2" x2="12" y2="6"></line>
                <line x1="12" y1="18" x2="12" y2="22"></line>
                <line x1="4.93" y1="4.93" x2="7.76" y2="7.76"></line>
                <line x1="16.24" y1="16.24" x2="19.07" y2="19.07"></line>
                <line x1="2" y1="12" x2="6" y2="12"></line>
                <line x1="18" y1="12" x2="22" y2="12"></line>
                <line x1="4.93" y1="19.07" x2="7.76" y2="16.24"></line>
                <line x1="16.24" y1="7.76" x2="19.07" y2="4.93"></line>
              </svg>
              {{ loading ? 'Buscando...' : 'Buscar' }}
            </button>

            <button type="button" @click="clearFilters" class="btn-clear" :disabled="loading">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <polyline points="3,6 5,6 21,6"></polyline>
                <path d="M19,6v14a2,2 0 0,1 -2,2H7a2,2 0 0,1 -2,-2V6m3,0V4a2,2 0 0,1 2,-2h4a2,2 0 0,1 2,2v2"></path>
              </svg>
              Limpar
            </button>
          </div>
        </form>
      </div>

      <div class="quotes-card">
        <div class="card-header">
          <h2>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
              <polyline points="14,2 14,8 20,8"></polyline>
              <line x1="16" y1="13" x2="8" y2="13"></line>
              <line x1="16" y1="17" x2="8" y2="17"></line>
              <polyline points="10,9 9,9 8,9"></polyline>
            </svg>
            Cotações
            <span class="quote-count">{{ quotes.length }}</span>
          </h2>
        </div>

        <div v-if="loading" class="loading-state">
          <div class="loading-spinner"></div>
          <p>Carregando cotações...</p>
        </div>

        <div v-else-if="quotes.length === 0" class="empty-state">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
            <circle cx="11" cy="11" r="8"></circle>
            <path d="M21 21l-4.35-4.35"></path>
          </svg>
          <h3>Nenhuma cotação encontrada</h3>
          <p>Ajuste os filtros para ver mais resultados</p>
        </div>

        <div v-else class="quotes-grid">
          <div v-for="quote in quotes" :key="quote.ide" class="quote-card">
            <div class="quote-header">
              <div class="quote-id">
                <a :href="`/quote-detail/${quote.ide}`" target="_blank" class="quote-link">
                  #{{ quote.ide.substring(0, 8) }}
                </a>
              </div>
              <div class="quote-status" :class="`status-${quote.situation}`">
                {{ getSituationText(quote.situation) }}
              </div>
            </div>

            <div class="quote-content">
              <div class="quote-info">
                <div class="info-item">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                    <circle cx="12" cy="7" r="4"></circle>
                  </svg>
                  <span>{{ quote.clientName }}</span>
                </div>

                <div class="info-item">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <rect x="2" y="3" width="20" height="14" rx="2" ry="2"></rect>
                    <line x1="8" y1="21" x2="16" y2="21"></line>
                    <line x1="12" y1="17" x2="12" y2="21"></line>
                  </svg>
                  <span>{{ quote.licensePlate }}</span>
                </div>

                <div class="info-item">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                    <line x1="16" y1="2" x2="16" y2="6"></line>
                    <line x1="8" y1="2" x2="8" y2="6"></line>
                    <line x1="3" y1="10" x2="21" y2="10"></line>
                  </svg>
                  <span>{{ formatDate(quote.createdDate) }}</span>
                </div>
              </div>

              <div class="quote-actions">
                <a :href="`/quote-detail/${quote.ide}`" target="_blank" class="btn-detail">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                    <circle cx="12" cy="12" r="3"></circle>
                  </svg>
                  Ver Detalhes
                </a>

                <a :href="`/quote-view/${quote.ide}`" target="_blank" class="btn-client-view">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                    <circle cx="12" cy="7" r="4"></circle>
                  </svg>
                  Visualização Cliente
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>

  </div>
</template>

<script>
import api from '../services/api'

export default {
  name: 'AdminPage',
  data() {
    return {
      quotes: [],
      loading: false,
      filters: {
        startDate: '',
        endDate: '',
        situation: 1
      }
    }
  },
  mounted() {
    // Definir datas padrão (últimos 30 dias)
    const today = new Date()
    const thirtyDaysAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000)

    this.filters.startDate = thirtyDaysAgo.toISOString().split('T')[0]
    this.filters.endDate = today.toISOString().split('T')[0]

    this.loadQuotes()
  },
  methods: {
    async loadQuotes() {
      this.loading = true
      try {
        const response = await api.post('/vehicle-insurance/quotes/filter', {
          startDate: this.filters.startDate,
          endDate: this.filters.endDate,
          situation: parseInt(this.filters.situation)
        })
        this.quotes = response.data
      } catch (error) {
        console.error('Erro ao carregar cotações:', error)
        this.quotes = []
      } finally {
        this.loading = false
      }
    },

    async applyFilters() {
      await this.loadQuotes()
    },

    clearFilters() {
      const today = new Date()
      const thirtyDaysAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000)

      this.filters = {
        startDate: thirtyDaysAgo.toISOString().split('T')[0],
        endDate: today.toISOString().split('T')[0],
        situation: 1
      }

      this.loadQuotes()
    },


    logout() {
      localStorage.removeItem('token')
      this.$router.push('/login')
    },

    formatDate(dateString) {
      const date = new Date(dateString)
      return date.toLocaleDateString('pt-BR')
    },

    getSituationText(situation) {
      const situations = {
        1: 'Recebido',
        2: 'Cotando',
        3: 'Cotação Concluída',
        4: 'Aprovado',
        5: 'Rejeitado',
        6: 'Arquivado'
      }
      return situations[situation] || 'Desconhecida'
    }
  }
}
</script>

<style scoped>
/* Reset e variáveis */
:root {
  --primary-color: #1a1a1a;
  --secondary-color: #f8f9fa;
  --accent-color: #007bff;
  --text-color: #2c3e50;
  --text-light: #6c757d;
  --border-color: #e9ecef;
  --shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  --shadow-lg: 0 4px 20px rgba(0, 0, 0, 0.15);
  --radius: 12px;
  --radius-sm: 8px;
  --transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.admin-container {
  min-height: 100vh;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  color: var(--text-color);
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

/* Header */
.admin-header {
  background: white;
  border-bottom: 1px solid var(--border-color);
  box-shadow: var(--shadow);
  position: sticky;
  top: 0;
  z-index: 100;
}

.header-content {
  max-width: 1200px;
  margin: 0 auto;
  padding: 1rem 2rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.header-title h1 {
  margin: 0;
  font-size: 1.75rem;
  font-weight: 700;
  color: var(--primary-color);
  letter-spacing: -0.025em;
}

.subtitle {
  display: block;
  font-size: 0.875rem;
  color: var(--text-light);
  font-weight: 500;
  margin-top: 0.25rem;
}

.btn-logout {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.25rem;
  background: white;
  color: var(--primary-color);
  border: 2px solid var(--primary-color);
  border-radius: var(--radius-sm);
  font-weight: 500;
  cursor: pointer;
  transition: var(--transition);
  font-size: 0.875rem;
}

.btn-logout:hover {
  background: #333;
  color: white !important;
  border-color: var(--primary-color) !important;
  transform: translateY(-1px);
  box-shadow: var(--shadow);
}

/* Main content */
.admin-main {
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

/* Cards */
.filters-card,
.quotes-card {
  background: white;
  border-radius: var(--radius);
  box-shadow: var(--shadow);
  overflow: hidden;
  border: 1px solid var(--border-color);
}

.card-header {
  padding: 1.5rem 2rem;
  border-bottom: 1px solid var(--border-color);
  background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
}

.card-header h2 {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--primary-color);
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.quote-count {
  background: var(--accent-color);
  color: white;
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
  margin-left: auto;
}

/* Filters */
.filters-form {
  padding: 2rem;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
  align-items: end;
}

.filter-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.filter-group label {
  font-weight: 600;
  color: var(--text-color);
  font-size: 0.875rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.filter-input {
  padding: 0.875rem 1rem;
  border: 2px solid var(--border-color);
  border-radius: var(--radius-sm);
  background: white;
  color: var(--text-color);
  font-size: 0.875rem;
  transition: var(--transition);
}

.filter-input:focus {
  outline: none;
  border-color: var(--accent-color);
  box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.1);
}

.filter-actions {
  display: flex;
  gap: 1rem;
  margin-top: 1.5rem;
}

.btn-search {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.875rem 1.5rem;
  background: #007bff;
  color: white;
  border: none;
  border-radius: var(--radius-sm);
  font-weight: 600;
  cursor: pointer;
  transition: var(--transition);
  font-size: 0.875rem;
  height: fit-content;
  min-width: 120px;
  box-shadow: 0 2px 4px rgba(0, 123, 255, 0.2);
}

.btn-search:hover:not(:disabled) {
  background: #ffc107;
  color: #000;
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(255, 193, 7, 0.3);
}

.btn-search:disabled {
  background: var(--text-light);
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

.btn-clear {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.875rem 1.5rem;
  background: white;
  color: var(--text-color);
  border: 2px solid var(--border-color);
  border-radius: var(--radius-sm);
  font-weight: 600;
  cursor: pointer;
  transition: var(--transition);
  font-size: 0.875rem;
  height: fit-content;
  min-width: 120px;
}

.btn-clear:hover:not(:disabled) {
  background: #f8f9fa;
  border-color: var(--text-light);
  transform: translateY(-1px);
  box-shadow: var(--shadow);
}

.btn-clear:disabled {
  background: #f8f9fa;
  color: var(--text-light);
  cursor: not-allowed;
  transform: none;
  border-color: #e9ecef;
}

.spinner {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
}

/* Quotes Grid */
.quotes-grid {
  padding: 2rem;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 1.5rem;
}

.quote-card {
  background: white;
  border: 1px solid var(--border-color);
  border-radius: var(--radius);
  padding: 1.5rem;
  transition: var(--transition);
  position: relative;
  overflow: hidden;
}

.quote-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(90deg, var(--accent-color), #28a745);
}

.quote-card:hover {
  transform: translateY(-2px);
  box-shadow: var(--shadow-lg);
  border-color: var(--accent-color);
}

.quote-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.quote-id {
  font-family: 'Monaco', 'Menlo', monospace;
  font-size: 0.875rem;
  color: var(--text-light);
  background: var(--secondary-color);
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
}

.quote-status {
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.status-1 {
  background: #d4edda;
  color: #155724;
}

.status-2 {
  background: #cce5ff;
  color: #004085;
}

.status-3 {
  background: #fff3cd;
  color: #856404;
}

.status-4 {
  background: #d1ecf1;
  color: #0c5460;
}

.status-5 {
  background: #f8d7da;
  color: #721c24;
}

.status-6 {
  background: #e2e3e5;
  color: #383d41;
}

.quote-content {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.quote-info {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.info-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-size: 0.875rem;
  color: var(--text-color);
}

.info-item svg {
  color: var(--text-light);
  flex-shrink: 0;
}

.quote-actions {
  margin-top: auto;
  padding-top: 1rem;
  border-top: 1px solid var(--border-color);
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.btn-detail {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
  padding: 0.75rem 1rem;
  background: #000000;
  color: white;
  border: none;
  border-radius: var(--radius-sm);
  font-weight: 500;
  cursor: pointer;
  transition: var(--transition);
  font-size: 0.875rem;
  text-decoration: none;
  text-align: center;
  justify-content: center;
}

.btn-detail:hover {
  background: #ffc107;
  color: #000000;
  transform: translateY(-1px);
  text-decoration: none;
}

.btn-client-view {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
  padding: 0.75rem 1rem;
  background: #10b981;
  color: white;
  border: none;
  border-radius: var(--radius-sm);
  font-weight: 500;
  cursor: pointer;
  transition: var(--transition);
  font-size: 0.875rem;
  text-decoration: none;
  text-align: center;
  justify-content: center;
}

.btn-client-view:hover {
  background: #059669;
  transform: translateY(-1px);
  color: white;
  text-decoration: none;
}

.quote-link {
  color: var(--primary-color);
  text-decoration: none;
  font-weight: 600;
  transition: var(--transition);
}

.quote-link:hover {
  color: #333;
  text-decoration: underline;
}

/* Loading e Empty States */
.loading-state,
.empty-state {
  padding: 4rem 2rem;
  text-align: center;
  color: var(--text-light);
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 3px solid var(--border-color);
  border-top: 3px solid var(--accent-color);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 1rem;
}

.empty-state svg {
  color: var(--text-light);
  margin-bottom: 1rem;
}

.empty-state h3 {
  margin: 0 0 0.5rem;
  color: var(--text-color);
  font-weight: 600;
}

.empty-state p {
  margin: 0;
  font-size: 0.875rem;
}



.status-badge {
  display: inline-block;
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  width: fit-content;
}

.price {
  font-weight: 700;
  color: #28a745;
}

/* Responsive */
@media (max-width: 768px) {
  .admin-main {
    padding: 1rem;
  }

  .filters-form {
    grid-template-columns: 1fr;
  }

  .quotes-grid {
    grid-template-columns: 1fr;
  }

}
</style>
